import type {
  IDistributionBucket,
  IQuestionDistribution,
  IQuestionTheme,
  QuestionDemographicDimension,
} from '@brushfire/types'
import { resolveDemographicSegmentDisplayLabel } from '@brushfire/types'

/**
 * Pure transforms from drill-down distribution payloads into Chart.js-friendly
 * shapes. Kept framework-free so they can be unit-tested without rendering.
 */

export interface SegmentSeries {
  /** Segment value label, e.g. `25-34`. */
  segment: string
  buckets: IDistributionBucket[]
}

export interface SegmentMatrixRow {
  segmentKey: string
  segmentLabel: string
  cells: Array<{ optionKey: string; optionLabel: string; percent: number }>
}

/** Matrix projection of `distribution.segments[dimension]` — rows = segments, cols = global options. */
export function buildSegmentMatrix(
  distribution: IQuestionDistribution,
  dimension: QuestionDemographicDimension,
): SegmentMatrixRow[] {
  const bySegment = distribution.segments[dimension]
  if (!bySegment) return []

  const optionOrder = distribution.global.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
  }))

  return Object.keys(bySegment)
    .sort((a, b) => a.localeCompare(b))
    .map((segmentKey) => {
      const buckets = bySegment[segmentKey] ?? []
      const bucketMap = new Map(buckets.map((bucket) => [bucket.key, bucket]))
      return {
        segmentKey,
        segmentLabel: resolveDemographicSegmentDisplayLabel(dimension, segmentKey),
        cells: optionOrder.map(({ key, label }) => ({
          optionKey: key,
          optionLabel: label,
          percent: bucketMap.get(key)?.percent ?? 0,
        })),
      }
    })
}

/** Highest count first — matches Figma horizontal bar ranking (5897:14387). */
export function bucketsByCountDesc(buckets: IDistributionBucket[]): IDistributionBucket[] {
  return [...buckets].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
}

/** Ordered segment series for a dimension (empty when the dimension has no data). */
export function segmentSeries(
  distribution: IQuestionDistribution,
  dimension: QuestionDemographicDimension,
): SegmentSeries[] {
  const bySegment = distribution.segments[dimension]
  if (!bySegment) return []
  return Object.keys(bySegment)
    .sort((a, b) => a.localeCompare(b))
    .map((segment) => ({
      segment: resolveDemographicSegmentDisplayLabel(dimension, segment),
      buckets: bySegment[segment],
    }))
}

function segmentResponseTotal(buckets: IDistributionBucket[]): number {
  return buckets.reduce((sum, bucket) => sum + bucket.count, 0)
}

/** Weighted mean of numeric bucket keys (slider values) for one segment. */
export function meanNumericBucketValue(buckets: IDistributionBucket[]): number {
  let sum = 0
  let total = 0
  for (const bucket of buckets) {
    const value = Number(bucket.key)
    if (!Number.isFinite(value)) continue
    sum += value * bucket.count
    total += bucket.count
  }
  if (total === 0) return 0
  return Math.round((sum / total) * 10) / 10
}

/**
 * Slider cross-tab — one vertical bar per demographic segment showing mean value.
 */
export function buildSegmentMeanBarDistribution(
  distribution: IQuestionDistribution,
  dimension: QuestionDemographicDimension,
): IQuestionDistribution {
  const bySegment = distribution.segments[dimension]
  if (!bySegment) return distribution

  const global: IDistributionBucket[] = Object.keys(bySegment)
    .sort((a, b) => a.localeCompare(b))
    .map((segmentKey) => {
      const buckets = bySegment[segmentKey] ?? []
      const mean = meanNumericBucketValue(buckets)
      const label = resolveDemographicSegmentDisplayLabel(dimension, segmentKey)
      return { key: segmentKey, label, count: mean, percent: 0 }
    })

  return { total: global.length, global, segments: {} }
}

/**
 * Slider × ordered demographic — line chart of mean slider value per ordered bin.
 */
export function buildSliderMeanLineDistribution(
  distribution: IQuestionDistribution,
  dimension: QuestionDemographicDimension,
): IQuestionDistribution {
  return buildSegmentMeanBarDistribution(distribution, dimension)
}

/**
 * Text cross-tab — themes as categories with per-segment counts for grouped bars.
 */
export function themesToCrossTabDistribution(
  themeSegments: Record<string, IQuestionTheme[]>,
  dimension: QuestionDemographicDimension,
): IQuestionDistribution {
  const themeLabels = new Map<string, string>()
  for (const themes of Object.values(themeSegments)) {
    for (const theme of themes) {
      if (!themeLabels.has(theme.theme)) themeLabels.set(theme.theme, theme.theme)
    }
  }

  const global: IDistributionBucket[] = [...themeLabels.keys()]
    .sort((a, b) => a.localeCompare(b))
    .map((theme) => ({ key: theme, label: theme, count: 0, percent: 0 }))

  const bySegment: Record<string, IDistributionBucket[]> = {}
  for (const [segmentKey, themes] of Object.entries(themeSegments)) {
    bySegment[segmentKey] = global.map((row) => {
      const match = themes.find((t) => t.theme === row.key)
      return {
        key: row.key,
        label: row.label,
        count: match?.count ?? 0,
        percent: match?.percent ?? 0,
      }
    })
  }

  return {
    total: global.length,
    global,
    segments: { [dimension]: bySegment },
  }
}

/**
 * Applies BFP-543 cross-tab data transforms before chart render (ranking, slider means, themes).
 */
export function prepareCrossTabDistribution(
  questionType: string,
  chartType: string,
  distribution: IQuestionDistribution,
  dimension: QuestionDemographicDimension,
): { distribution: IQuestionDistribution; dimension: QuestionDemographicDimension | null } {
  const normalizedType = questionType.trim().toLowerCase()
  let result = distribution
  let renderDimension: QuestionDemographicDimension | null = dimension

  if (chartType === 'vertical_column' && normalizedType === 'slider') {
    result = buildSegmentMeanBarDistribution(distribution, dimension)
    renderDimension = null
  } else if (chartType === 'multi_line' && normalizedType === 'slider') {
    result = buildSliderMeanLineDistribution(distribution, dimension)
    renderDimension = null
  }

  return { distribution: result, dimension: renderDimension }
}

/**
 * Keeps only the top-N demographic segments by respondent count (BFP-543
 * high-cardinality cross-tabs).
 */
export function distributionWithTopSegments(
  distribution: IQuestionDistribution,
  dimension: QuestionDemographicDimension,
  topN: number,
): IQuestionDistribution {
  const bySegment = distribution.segments[dimension]
  if (!bySegment) return distribution
  const rankedKeys = Object.keys(bySegment)
    .map((key) => ({ key, total: segmentResponseTotal(bySegment[key] ?? []) }))
    .sort((a, b) => b.total - a.total || a.key.localeCompare(b.key))
    .slice(0, topN)
    .map((row) => row.key)
  if (rankedKeys.length >= Object.keys(bySegment).length) return distribution
  const filtered: Record<string, IDistributionBucket[]> = {}
  for (const key of rankedKeys) {
    filtered[key] = bySegment[key] ?? []
  }
  return {
    ...distribution,
    segments: { ...distribution.segments, [dimension]: filtered },
  }
}

/**
 * Union of bucket labels across the global distribution and every segment, in
 * stable global order first. Used as the category axis for grouped/stacked.
 */
export function bucketLabelOrder(
  distribution: IQuestionDistribution,
  series: SegmentSeries[],
): Array<{ key: string; label: string }> {
  const seen = new Map<string, string>()
  for (const b of distribution.global) {
    if (!seen.has(b.key)) seen.set(b.key, b.label)
  }
  for (const s of series) {
    for (const b of s.buckets) {
      if (!seen.has(b.key)) seen.set(b.key, b.label)
    }
  }
  return [...seen.entries()].map(([key, label]) => ({ key, label }))
}

/** Looks up a count for a bucket key within a segment's buckets (0 when absent). */
export function countForKey(buckets: IDistributionBucket[], key: string): number {
  return buckets.find((b) => b.key === key)?.count ?? 0
}

/** Empty rows between option groups — segment bars within a group stay flush. */
export const GROUPED_HORIZONTAL_SPACER_ROWS = 2
/** Vertical size of each spacer row in the plot height calculation (Figma 5897:15117). */
export const GROUPED_HORIZONTAL_SPACER_ROW_PX = 10

export interface GroupedHorizontalBarRowMeta {
  optionLabel: string
  segmentLabel: string
  percent: number
}

export interface GroupedHorizontalBarModel {
  labels: string[]
  rowMeta: Array<GroupedHorizontalBarRowMeta | null>
  datasets: Array<{
    label: string
    data: Array<number | null>
    backgroundColor: string
  }>
  plotHeight: number
}

/**
 * Expands each answer option into stacked segment rows (Figma 5897:15117) instead
 * of side-by-side grouped bars. One dataset per demographic segment; spacer rows
 * create the gap between option blocks.
 */
function appendGroupedHorizontalSpacerRows(
  labels: string[],
  rowMeta: Array<GroupedHorizontalBarRowMeta | null>,
): void {
  for (let spacer = 0; spacer < GROUPED_HORIZONTAL_SPACER_ROWS; spacer++) {
    labels.push('')
    rowMeta.push(null)
  }
}

function pushGroupedHorizontalRowMeta(
  labels: string[],
  rowMeta: Array<GroupedHorizontalBarRowMeta | null>,
  option: { key: string; label: string },
  segment: ReturnType<typeof segmentSeries>[number],
  segmentIndex: number,
): void {
  const count = countForKey(segment.buckets, option.key)
  const segmentTotal = segment.buckets.reduce((sum, b) => sum + b.count, 0)
  labels.push(segmentIndex === 0 ? option.label : '')
  rowMeta.push({
    optionLabel: option.label,
    segmentLabel: segment.segment,
    percent: segmentTotal > 0 ? Math.round((count / segmentTotal) * 100) : 0,
  })
}

function buildGroupedHorizontalRowMeta(
  order: ReturnType<typeof bucketLabelOrder>,
  series: ReturnType<typeof segmentSeries>,
): { labels: string[]; rowMeta: Array<GroupedHorizontalBarRowMeta | null> } {
  const labels: string[] = []
  const rowMeta: Array<GroupedHorizontalBarRowMeta | null> = []
  const segmentCount = series.length
  const optionCount = order.length

  for (let optionIndex = 0; optionIndex < optionCount; optionIndex++) {
    const option = order[optionIndex]
    if (!option) continue
    for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
      const segment = series[segmentIndex]
      if (!segment) continue
      pushGroupedHorizontalRowMeta(labels, rowMeta, option, segment, segmentIndex)
    }
    if (optionIndex < optionCount - 1) {
      appendGroupedHorizontalSpacerRows(labels, rowMeta)
    }
  }

  return { labels, rowMeta }
}

function appendGroupedHorizontalSpacerData(data: Array<number | null>): void {
  for (let spacer = 0; spacer < GROUPED_HORIZONTAL_SPACER_ROWS; spacer++) {
    data.push(null)
  }
}

function buildGroupedHorizontalDatasets(
  order: ReturnType<typeof bucketLabelOrder>,
  series: ReturnType<typeof segmentSeries>,
  colorForSegment: (index: number, segmentCount: number) => string,
) {
  const segmentCount = series.length
  const optionCount = order.length

  return series.map((segment, segmentIndex) => {
    const data: Array<number | null> = []
    for (let optionIndex = 0; optionIndex < optionCount; optionIndex++) {
      const option = order[optionIndex]
      if (!option) continue
      for (let rowSegmentIndex = 0; rowSegmentIndex < segmentCount; rowSegmentIndex++) {
        data.push(
          rowSegmentIndex === segmentIndex
            ? countForKey(segment.buckets, option.key)
            : null,
        )
      }
      if (optionIndex < optionCount - 1) {
        appendGroupedHorizontalSpacerData(data)
      }
    }
    return {
      label: segment.segment,
      data,
      backgroundColor: colorForSegment(segmentIndex, segmentCount),
    }
  })
}

export function buildGroupedHorizontalBarModel(
  distribution: IQuestionDistribution,
  dimension: QuestionDemographicDimension,
  colorForSegment: (index: number, segmentCount: number) => string,
  plotHeightFor: (optionCount: number, segmentCount: number) => number,
): GroupedHorizontalBarModel {
  const series = segmentSeries(distribution, dimension)
  const order = bucketLabelOrder(distribution, series)
  const segmentCount = series.length
  const optionCount = order.length

  if (segmentCount === 0 || optionCount === 0) {
    return { labels: [], rowMeta: [], datasets: [], plotHeight: plotHeightFor(0, 0) }
  }

  const { labels, rowMeta } = buildGroupedHorizontalRowMeta(order, series)
  const datasets = buildGroupedHorizontalDatasets(order, series, colorForSegment)

  return {
    labels,
    rowMeta,
    datasets,
    plotHeight: plotHeightFor(optionCount, segmentCount),
  }
}

/** Sorts themes by descending count, then alphabetically, capped to `limit`. */
export function topThemes(themes: IQuestionTheme[], limit = 10): IQuestionTheme[] {
  return [...themes]
    .sort((a, b) => b.count - a.count || a.theme.localeCompare(b.theme))
    .slice(0, limit)
}
